// Gerencia preferências de acessibilidade persistidas em localStorage.
// Aplica classes no <body> que disparam estilos CSS condicionais.

import { hapticManager } from './haptic';
import { mascotManager } from './mascot';
import { ttsManager, getReadableActivityText } from './tts';
import { audioManager } from './audio';

const STORAGE_KEY = 'atividades.a11y.v1';

interface Prefs {
  highContrast: boolean;
  largeFont: boolean;
  ttsEnabled: boolean;
  mascotEnabled: boolean;
  hapticEnabled: boolean;
  muted: boolean;
}

const DEFAULTS: Prefs = {
  highContrast: false,
  largeFont: false,
  ttsEnabled: false,
  mascotEnabled: true,
  hapticEnabled: true,
  muted: false,
};

function load(): Prefs {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULTS };
    const parsed = JSON.parse(raw) as Partial<Prefs>;
    return { ...DEFAULTS, ...parsed };
  } catch {
    return { ...DEFAULTS };
  }
}

function save(prefs: Prefs): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // localStorage cheio ou disabled — silenciar
  }
}

class AccessibilityImpl {
  private prefs: Prefs;
  private panelEl: HTMLElement | null = null;

  constructor() {
    this.prefs = load();
  }

  init(): void {
    this.applyAll();
    this.injectUI();
  }

  private applyAll(): void {
    document.body.classList.toggle('a11y--high-contrast', this.prefs.highContrast);
    document.body.classList.toggle('a11y--large-font', this.prefs.largeFont);
    ttsManager.setEnabled(this.prefs.ttsEnabled);
    mascotManager.setEnabled(this.prefs.mascotEnabled);
    hapticManager.setEnabled(this.prefs.hapticEnabled);
    audioManager.setMuted(this.prefs.muted);
  }

  private set<K extends keyof Prefs>(key: K, value: Prefs[K]): void {
    this.prefs[key] = value;
    save(this.prefs);
    this.applyAll();
    this.refreshPanel();
  }

  private injectUI(): void {
    // Botão ♿ flutuante no canto superior direito
    const btn = document.createElement('button');
    btn.className = 'a11y-toggle';
    btn.type = 'button';
    btn.setAttribute('aria-label', 'Configurações de acessibilidade');
    btn.textContent = '♿';
    btn.addEventListener('click', () => this.togglePanel());
    document.body.appendChild(btn);

    // Botão 🔊 "Ler em voz alta" — só aparece se TTS suportado
    if (ttsManager.isSupported()) {
      const speak = document.createElement('button');
      speak.className = 'a11y-speak';
      speak.type = 'button';
      speak.setAttribute('aria-label', 'Ler instrução em voz alta');
      speak.textContent = '🔊';
      speak.addEventListener('click', () => {
        if (!this.prefs.ttsEnabled) {
          // Ativa TTS no primeiro clique
          this.set('ttsEnabled', true);
        }
        ttsManager.speak(getReadableActivityText());
      });
      document.body.appendChild(speak);
    }
  }

  private togglePanel(): void {
    if (this.panelEl) {
      this.panelEl.remove();
      this.panelEl = null;
      return;
    }
    const panel = document.createElement('div');
    panel.className = 'a11y-panel';
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-label', 'Configurações de acessibilidade');
    panel.innerHTML = `
      <div class="a11y-panel__header">
        <h2>Acessibilidade</h2>
        <button class="a11y-panel__close" type="button" aria-label="Fechar">✕</button>
      </div>
      <div class="a11y-panel__body">
        ${this.renderToggle('highContrast', 'Alto contraste', 'Cores fortes pra baixa visão')}
        ${this.renderToggle('largeFont', 'Fonte grande', 'Aumenta o tamanho do texto')}
        ${this.renderToggle('ttsEnabled', 'Leitor (TTS)', ttsManager.isSupported() ? 'Lê instruções em voz alta' : 'Não suportado neste navegador')}
        ${this.renderToggle('mascotEnabled', 'Mascote', 'Mostra reação animada no canto')}
        ${this.renderToggle('hapticEnabled', 'Vibração', 'Vibra no celular nos acertos/erros')}
        ${this.renderToggle('muted', 'Sem som', 'Desliga todos os sons')}
      </div>
    `;
    document.body.appendChild(panel);
    this.panelEl = panel;
    panel.querySelector<HTMLElement>('.a11y-panel__close')!.addEventListener('click', () => this.togglePanel());
    panel.querySelectorAll<HTMLInputElement>('input[type="checkbox"][data-pref]').forEach((input) => {
      input.addEventListener('change', () => {
        const key = input.dataset['pref'] as keyof Prefs;
        this.set(key, input.checked);
      });
    });
  }

  private renderToggle(key: keyof Prefs, label: string, desc: string): string {
    const checked = this.prefs[key] ? 'checked' : '';
    const disabled = key === 'ttsEnabled' && !ttsManager.isSupported() ? 'disabled' : '';
    return `
      <label class="a11y-toggle-row">
        <input type="checkbox" data-pref="${key}" ${checked} ${disabled} />
        <span class="a11y-toggle-row__text">
          <strong>${label}</strong>
          <small>${desc}</small>
        </span>
      </label>
    `;
  }

  private refreshPanel(): void {
    if (!this.panelEl) return;
    // Atualiza visualmente o checkbox de cada toggle (caso outro caminho mude state)
    this.panelEl.querySelectorAll<HTMLInputElement>('input[type="checkbox"][data-pref]').forEach((input) => {
      const key = input.dataset['pref'] as keyof Prefs;
      input.checked = !!this.prefs[key];
    });
  }
}

export const accessibility = new AccessibilityImpl();
